import {
  booleanAttribute,
  Component,
  computed,
  effect,
  input,
  linkedSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AttachmentModel,
  MediaThumbnail,
} from '@fc-vehicles/service/attachment-model';
import { SkeletonModule } from '@fc-shared/ui/skeleton/skeleton.module';
import {
  buildThumbnailSrcset,
  hasThumbnailTiers,
  ThumbnailTier,
  warnMissingThumbnails,
} from '@fc-shared/ui/image-loader/thumbnail-tiers';

@Component({
  selector: 'fc-image-view',
  imports: [SkeletonModule],
  host: {
    '[style.width]': 'hostWidth()',
    '[style.height]': 'hostHeight()',
  },
  template: `
    @if (hasImage()) {
      <img
        [style.width]="hostWidth()"
        [style.height]="hostHeight()"
        [src]="imageSource()"
        [attr.srcset]="srcSet()"
        [attr.sizes]="sizesAttr()"
        (load)="imageLoaded.set(true)"
        [class]="[shape(), 'object-cover']"
        [style.borderRadius]="borderRadius()"
        [attr.loading]="priority() ? 'eager' : 'lazy'"
        [attr.fetchpriority]="priority() ? 'high' : null"
        decoding="async"
        [alt]="alt()"
      />
      @if (!imageLoaded()) {
        <div class="absolute top-0 left-0 w-full h-full">
          <fc-skeleton
            [shape]="shape()"
            [style.width]="hostWidth()"
            [style.height]="hostHeight()"
            [style.borderRadius]="borderRadius()"
            animation="wave"
          ></fc-skeleton>
        </div>
      }
    } @else {
      <div
        [style.width]="hostWidth()"
        [style.height]="hostHeight()"
        [style.backgroundImage]="'url(' + imagePlaceholder() + ')'"
        [class]="'placeholder ' + shape()"
        [style.borderRadius]="borderRadius()"
      ></div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    @use 'var' as *;

    :host {
      position: relative;
      display: block;
    }

    .placeholder {
      background-color: $secondary-100;
      background-repeat: no-repeat;
      background-size: 70%;
      background-position: center;
    }

    .circle {
      border-radius: 50%;
    }

    .rectangle {
      border-radius: 4px;
    }
  `,
})
export class ImageViewComponent {
  imagePlaceholder = input<string>('/assets/icons/vehicle-icon.svg');
  size = input<number | null>(null);
  width = input<number>(40);
  height = input<number>(40);
  photo = input<AttachmentModel | null>(null);
  image = input<string | null>(null);
  shape = input<'circle' | 'rectangle'>('circle');

  /** Overrides the shape's default corner radius (rectangle: 4px). */
  cornerRadius = input<number | null>(null);
  thumbnailStyle = input<'square' | 'thumbnails'>('thumbnails');

  /**
   * Stretches to the parent box (100% x 100%) instead of the fixed pixel
   * sizing, for fluid containers like camera preview tiles.
   */
  fill = input(false, { transform: booleanAttribute });

  protected borderRadius = computed(() => {
    const radius = this.cornerRadius();
    return radius == null ? null : `${radius}px`;
  });

  protected hostWidth = computed(() =>
    this.fill() ? '100%' : `${this.size() || this.width()}px`,
  );
  protected hostHeight = computed(() =>
    this.fill() ? '100%' : `${this.size() || this.height()}px`,
  );

  /** Accessible description of the image. */
  alt = input<string>('photo');

  /**
   * Mark an above-the-fold / LCP image so it loads eagerly with
   * `fetchpriority="high"`. Leave `false` (default) for everything else so it
   * stays lazy — never set this on off-screen or list thumbnails.
   */
  priority = input<boolean>(false);

  /**
   * `'auto'` (default) emits a full `srcset` and lets the browser pick the tier
   * for the rendered size and DPR. An explicit tier pins `src` to that single
   * URL; `'original'` uses the full-resolution image.
   */
  thumbnailSize = input<ThumbnailTier | 'auto' | 'original'>('auto');
  private isAuto = computed(() => this.thumbnailSize() === 'auto');

  constructor() {
    effect(() => {
      const photo = this.photo();
      if (!photo || this.thumbnailSize() === 'original') return;
      const group = photo[this.thumbnailStyle()] as MediaThumbnail | undefined;
      if (!hasThumbnailTiers(group)) warnMissingThumbnails(photo);
    });
  }

  private photoUrl = computed(() => {
    const photo = this.photo();
    return photo?.previewImg || photo?.original;
  });

  /** The `src` URL: a full image in auto mode, or the pinned tier otherwise. */
  imageSource = computed(() => {
    const source = this.photoUrl() || this.image() || '';
    const tier = this.thumbnailSize();
    const photo = this.photo();
    if (tier === 'auto' || tier === 'original' || !photo) return source;
    const group = photo[this.thumbnailStyle()] as MediaThumbnail | undefined;
    return group?.[tier]?.url ?? source;
  });

  hasImage = computed(() => this.imageSource().length > 0);

  /** Candidate tiers (auto mode only) as `<url> <intrinsic-width>w` entries. */
  srcSet = computed(() => {
    if (!this.isAuto()) return null;
    const photo = this.photo();
    if (!photo) return null;
    const list = this.photoSrcset(photo);
    return list.length ? list.join(', ') : null;
  });

  /** Tells the browser the rendered size so it can choose from `srcSet`. */
  sizesAttr = computed(() => {
    if (!this.srcSet() || this.fill()) return null;
    const renderPx = this.size() || this.width() || this.height() || 0;
    return renderPx ? `${renderPx}px` : null;
  });

  // Reset to false whenever the source changes (e.g. switching vehicles) so the
  // skeleton shows while the new image loads instead of painting the stale one.
  imageLoaded = linkedSignal<string, boolean>({
    source: this.imageSource,
    computation: () => false,
  });

  /**
   * Backend-generated thumbnail tiers, plus the original as the largest entry.
   */
  private photoSrcset(photo: AttachmentModel): string[] {
    const group = photo[this.thumbnailStyle()] as MediaThumbnail | undefined;
    const list = buildThumbnailSrcset(group);

    const originalUrl = photo.previewImg || photo.original;
    if (originalUrl && photo.previewImgWidth) {
      list.push(`${originalUrl} ${photo.previewImgWidth}w`);
    }

    return list;
  }
}
